import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Copy, Link2, Plus, Search, SlidersHorizontal } from 'lucide-react';
// Impersonate is off for Solution Partners: a partner works beside the client,
// never as them. The pieces stay commented, not deleted, in case it returns.
// import { LogIn, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { createReferralLink } from '../../services/api';
// import { impersonateClient } from '../../services/api';
// import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import type { PartnerClientRow, PartnerOverview } from '../../types/aai';
import {
  Action, Banner, EmptyState, ErrorNote, FilterChips, Loading, PageBody, PageHead, Panel, StatusChip,
} from '../../components/aa';
import { DownloadOrbit } from './DownloadOrbit';
import { STATUS_CHIP, STATUS_EXPLAINED, STATUS_LABEL, filterOf, relativeTime } from './format';

/*
 * Clients: every client the Solution Partner looks after, with a real status.
 *
 * The status is derived on the server from recorded facts (invite issued,
 * invite opened, intake complete) rather than guessed from a stage band, so
 * "Prospect" here means nobody has been invited and "Active" means onboarding is
 * done. Opening a row selects that client for every scoped page.
 */

type FilterId = 'all' | 'active' | 'draft' | 'onboarding';

const FILTER_IDS: FilterId[] = ['all', 'active', 'draft', 'onboarding'];

const filterFrom = (raw: string | null): FilterId => {
  if (raw && (FILTER_IDS as string[]).includes(raw)) return raw as FilterId;
  return 'all';
};

/*
 * The order. Newest first by default, because the reason a partner opens this
 * list right after adding a client is that client, and alphabetical order put
 * it wherever its name happened to fall.
 */
type SortId = 'newest' | 'oldest' | 'name';

const SORTS: { id: SortId; label: string }[] = [
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'name', label: 'A to Z' },
];

const sortFrom = (raw: string | null): SortId => {
  const found = SORTS.find((s) => s.id === raw);
  if (found) return found.id;
  return 'newest';
};

/* A client with no created_at sorts last either way rather than jumping to the
   top of "newest" on an empty string comparison. */
const added = (row: PartnerClientRow): number => {
  if (!row.created_at) return 0;
  const at = Date.parse(row.created_at);
  if (Number.isNaN(at)) return 0;
  return at;
};

const compareBy = (sort: SortId) => (a: PartnerClientRow, b: PartnerClientRow): number => {
  if (sort === 'name') return a.name.localeCompare(b.name);
  if (sort === 'oldest') return added(a) - added(b);
  return added(b) - added(a);
};

/*
 * The client link. It is not bound to a client, because the client does not
 * exist yet: whoever sets up a company through it lands under this partner.
 * Minted and copied on click, nothing stored, so a fresh one is a click away.
 * Direct entry is the same public front door without the referral, opened in
 * its own tab so the partner keeps this workspace behind it.
 */
const REFERRAL_DAYS = 30;

const DirectClientPath: React.FC = () => {
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const prepare = async () => {
    setBusy(true);
    try {
      const url = link || (await createReferralLink(REFERRAL_DAYS)).url;
      setLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Client link copied. It works for ${REFERRAL_DAYS} days.`);
      } catch {
        toast.info('The link is ready below. Select it and copy it yourself.');
      }
    } catch (err) {
      toast.error((err as Error).message || 'Could not prepare the client link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Banner
      tone="quiet"
      size="md"
      kicker="Direct client path"
      title="Clients can start with Orbit themselves"
      desc={
        link
          ? <span className="break-all font-sans text-[12px] text-ink">{link}</span>
          : 'Share your client link. The client sets up their company and onboards with Orbit on their own, and they are added to your clients.'
      }
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Action variant="quiet" icon={link ? <Copy size={14} /> : <Link2 size={14} />} onClick={prepare} disabled={busy} reason="Preparing the link">
            {link ? 'Copy client link' : 'Prepare client link'}
          </Action>
          <Action variant="ghost" href="/start">Open direct entry <ArrowUpRight size={14} /></Action>
        </div>
      }
    />
  );
};

const Cell: React.FC<{ width?: number; children: React.ReactNode; className?: string }> = ({
  width, children, className = '',
}) => (
  <div className={clsx('min-w-0', width ? 'shrink-0' : 'flex-1', className)} style={width ? { width } : undefined}>
    {children}
  </div>
);

/** What the columns add up to, below which the list scrolls sideways. */
const TABLE_MIN = 'min-w-[1380px]';

const COLUMNS: { label: string; width?: number }[] = [
  { label: 'Company', width: 176 },
  { label: 'Contact', width: 128 },
  { label: 'Status', width: 112 },
  { label: 'Onboarding', width: 168 },
  { label: 'Projects', width: 72 },
  { label: 'Last activity' },
  { label: 'Attention', width: 160 },
  // { label: '', width: 128 }, // Impersonate column
];

/* The row was one big button until it needed a second action inside it, and a
   button inside a button is not a thing the browser will render. The cells are
   the button now; Impersonate used to sit beside it and is commented out. */
const ClientRow: React.FC<{
  row: PartnerClientRow;
  striped: boolean;
  onOpen: () => void;
  // onImpersonate: () => void;
  // busy: boolean;
  // disabled: boolean;
}> = ({ row, striped, onOpen }) => (
  <div
    className={clsx(
      'flex w-full items-center gap-3 px-6 py-3.5 text-[13px] transition-colors hover:bg-canvas',
      striped && 'bg-card',
    )}
  >
    <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left text-[13px]">
    <Cell width={176}><div className="truncate font-semibold text-ink">{row.name}</div></Cell>
    <Cell width={128}><div className="truncate text-ink-muted">{row.contact_name || row.contact_email || 'Not yet named'}</div></Cell>
    <Cell width={112}><StatusChip status={STATUS_CHIP[row.status]} label={STATUS_LABEL[row.status]} /></Cell>
    <Cell width={168}><div className="truncate text-ink-muted" title={row.onboarding_label}>{row.onboarding_label}</div></Cell>
    <Cell width={72}><div className="font-sans text-[13px] font-medium tabular-nums text-ink">{row.projects_open}</div></Cell>
    <Cell>
      <div className="truncate text-[12.5px] text-ink-dim">
        {row.last_activity ? `${row.last_activity.label} · ${relativeTime(row.last_activity.at)}` : 'No activity yet'}
      </div>
    </Cell>
    <Cell width={160}>
      {row.attention && (
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#8A6D00]">
          <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-accent-500" />
          <span className="truncate">{row.attention.label}</span>
        </span>
      )}
      {!row.attention && <span className="text-[12px] text-ink-soft">None</span>}
    </Cell>
    <ArrowRight size={16} className="shrink-0 text-ink-soft" />
    </button>
    {/* Impersonate, off for Solution Partners.
    <div className="w-[128px] shrink-0 pl-3">
      <Action
        size="sm"
        variant="quiet"
        onClick={onImpersonate}
        disabled={disabled}
        reason="Starting the session"
        icon={busy ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />}
      >
        Impersonate
      </Action>
    </div>
    */}
  </div>
);

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { active, setActive, loading: clientsLoading } = useActiveClient();
  // const { startImpersonation } = useImpersonation();
  // const [becoming, setBecoming] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const filter = filterFrom(params.get('filter'));
  const sort = sortFrom(params.get('sort'));

  const overview = useAsync<PartnerOverview>(
    () => aaiApi.getPartnerOverview(), 'Could not load your clients.', [],
  );

  const rows = useMemo(() => {
    const all = overview.data?.clients || [];
    const needle = query.trim().toLowerCase();
    return all
      .filter((row) => filter === 'all' || filterOf(row.status) === filter)
      .filter((row) => !needle || row.name.toLowerCase().includes(needle) || (row.contact_name || '').toLowerCase().includes(needle))
      .sort(compareBy(sort));
  }, [overview.data, filter, query, sort]);

  const setFilter = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('filter');
    if (id !== 'all') next.set('filter', id);
    setParams(next, { replace: true });
  };

  const setSort = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === 'newest') next.delete('sort');
    if (id !== 'newest') next.set('sort', id);
    setParams(next, { replace: true });
  };

  const open = (row: PartnerClientRow) => {
    setActive(row.id);
    navigate('/partner/client');
  };

  /* Into the client's own workspace, as the client. Off for Solution Partners:
     a partner works beside the client, never as them. Kept commented in case
     it returns.
  const impersonate = async (row: PartnerClientRow) => {
    setBecoming(row.id);
    try {
      const data = await impersonateClient(row.id);
      toast.success(`Working as ${row.name}`);
      startImpersonation({
        access_token: data.access_token,
        user: {
          user_id: data.user.user_id,
          client_id: data.user.client_id,
          name: row.name,
          email: row.contact_email || undefined,
        },
        role: 'client',
        land: '/dashboard',
        returnTo: '/partner/clients',
      });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || `Could not start a session as ${row.name}`);
      setBecoming(null);
    }
  };
  */

  if (clientsLoading || overview.loading) return <PageBody><Loading label="Loading your clients" /></PageBody>;
  if (overview.error || !overview.data) {
    return <PageBody><ErrorNote message={overview.error || 'Could not load your clients.'} /></PageBody>;
  }

  const counts = overview.data.counts;
  /* In the order a company moves through them, so the row of chips reads as
     the lifecycle it is rather than an alphabet. */
  const filters = [
    { id: 'all', label: 'All', count: overview.data.clients.length },
    { id: 'draft', label: 'Prospects', count: counts.draft },
    { id: 'onboarding', label: 'Onboarding', count: counts.onboarding },
    { id: 'active', label: 'Active', count: counts.active },
  ];

  return (
    <PageBody>
      <PageHead
        eyebrow="Solution Partner"
        align="center"
        title="Clients"
        sub="Every client you look after, in one list. Open a client to work inside it: their intake, projects, people and documents."
        actions={
          <>
            {/* The framework decides what every client on this list is asked,
                so the way to it belongs beside the list rather than only
                inside one client. It carries no client: this is the version
                new clients inherit. */}
            <Action variant="quiet" icon={<SlidersHorizontal size={14} />} to="/partner/framework?back=%2Fpartner%2Fclients">
              Intake framework
            </Action>
            {active && <DownloadOrbit clientId={active.id} clientName={active.name} />}
            <Action icon={<Plus size={14} />} to="/partner/clients/new">Add client</Action>
          </>
        }
      />

      <div className="space-y-5">
        <DirectClientPath />

        <Panel>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line-soft px-4 py-4 sm:px-6">
            <FilterChips tabs={filters} active={filter} onChange={setFilter} label="Client status" />
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <label className="flex h-9 min-w-0 flex-1 items-center gap-2 sm:w-[260px] sm:flex-none rounded-act border border-line-strong bg-white px-3 text-[13px] text-ink">
                <Search size={15} className="shrink-0 text-ink-soft" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a client"
                  className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:ring-0"
                />
              </label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label="Order the list"
                className="h-9 shrink-0 rounded-act border border-line-strong bg-white pl-2.5 pr-8 text-[13px] text-ink outline-none focus:border-ink"
              >
                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Eight columns need room. Squeezed narrower than this the flexible
              column collapsed and a header like LAST ACTIVITY, one unbreakable
              word, spilled over the one beside it. Now the table scrolls
              sideways instead of printing two labels on top of each other. */}
          <div className="overflow-x-auto">
            <div className={TABLE_MIN}>
              <div className="flex items-center gap-3 border-b border-line-soft px-6 py-2.5">
                {COLUMNS.map((col) => (
                  <Cell key={col.label} width={col.width}>
                    <div className="truncate text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{col.label}</div>
                  </Cell>
                ))}
                <span className="w-4 shrink-0" />
              </div>
              {rows.map((row, i) => (
                <ClientRow
                  key={row.id}
                  row={row}
                  striped={i % 2 === 1}
                  onOpen={() => open(row)}
                />
              ))}
            </div>
          </div>

          {overview.data.clients.length === 0 && (
            <EmptyState
              title="No clients yet"
              reason="Add a client to start their onboarding, or share the direct client link and they will appear here."
              action={<Action to="/partner/clients/new">Add client</Action>}
            />
          )}
          {overview.data.clients.length > 0 && rows.length === 0 && (
            <EmptyState title="Nothing in this view" reason="No client matches this filter and search." />
          )}
        </Panel>

        <div className="flex flex-wrap gap-x-6 gap-y-1 px-1 text-[12px] text-ink-dim">
          {(['draft', 'invited', 'onboarding', 'active'] as const).map((status) => (
            <span key={status}><b className="text-ink">{STATUS_LABEL[status]}</b>: {STATUS_EXPLAINED[status]}</span>
          ))}
        </div>
      </div>
    </PageBody>
  );
};

export default ClientsPage;
